import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, FileText, Download, Send, Trash2, Unlock, MapPin, CalendarDays, Mail, Phone, Eye, CheckCircle2, AlertTriangle } from "lucide-react";
import { EmailPreview } from "@/components/EmailPreview";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";

const FILTERS = [
  { key: "all", label: "Tous" },
  { key: "pending_docs", label: "Documents" },
  { key: "transmitted", label: "Transmis" },
  { key: "confirmed", label: "Confirmés" },
  { key: "enrolled", label: "Inscrits" },
  { key: "released", label: "Libérés" },
];

const STATUS_BADGE = {
  pending_docs: "bg-amber-50 text-amber-700 border-amber-200",
  transmitted: "bg-blue-50 text-blue-700 border-blue-200",
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  enrolled: "bg-emerald-100 text-emerald-800 border-emerald-300",
  released: "bg-neutral-100 text-neutral-500 border-neutral-200",
  cancelled: "bg-red-50 text-red-600 border-red-200",
};
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminReservations() {
  const [data, setData] = useState({ items: [], counts: {} });
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [acting, setActing] = useState(false);
  const [emails, setEmails] = useState([]);
  const [previewId, setPreviewId] = useState(null);
  const closePreview = useCallback(() => setPreviewId(null), []);

  const loadEmails = useCallback((rid) => {
    if (!rid) { setEmails([]); return; }
    api.get(`/admin/reservations/${rid}/emails`).then(({ data }) => setEmails(data || [])).catch(() => setEmails([]));
  }, []);

  useEffect(() => { loadEmails(selected?.id); }, [selected?.id, selected?.status, loadEmails]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/reservations", { params: { status: filter, q: q || undefined } })
      .then(({ data }) => setData(data || { items: [], counts: {} }))
      .catch(() => toast.error("Impossible de charger les dossiers."))
      .finally(() => setLoading(false));
  }, [filter, q]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (rid) => api.get(`/admin/reservations/${rid}`).then(({ data }) => setSelected(data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));

  const act = async (rid, action) => {
    const confirmMsg = {
      transfer: "Transférer ce dossier à l'auto-école et envoyer l'email de confirmation OUI/NON à l'élève ?",
      release: "Libérer cette place ? L'élève sera informé et la place rendue disponible.",
      delete: "Supprimer définitivement ce dossier de la base ?",
    }[action];
    if (!window.confirm(confirmMsg)) return;
    setActing(true);
    try {
      if (action === "delete") {
        await api.delete(`/admin/reservations/${rid}`);
        toast.success("Dossier supprimé.");
        setSelected(null);
      } else {
        const { data } = await api.post(`/admin/reservations/${rid}/${action}`);
        setSelected(data);
        toast.success(action === "transfer" ? "Dossier transféré." : "Place libérée.");
      }
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setActing(false); }
  };

  return (
    <Shell role="admin">
      <div className="mb-5">
        <h1 className="text-2xl font-black tracking-tight text-neutral-900">Dossiers élèves</h1>
        <p className="mt-1 text-sm text-neutral-500">Réservations de places en permis accéléré. Transfère les dossiers complets à l'auto-école.</p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`resa-filter-${f.key}`}
            className={`rounded-full px-3 py-1.5 text-xs font-bold ${filter === f.key ? "bg-brand text-white" : "bg-neutral-100 text-neutral-600"}`}>
            {f.label}{data.counts?.[f.key] != null ? ` (${data.counts[f.key]})` : f.key === "all" && data.counts?.total != null ? ` (${data.counts.total})` : ""}
          </button>
        ))}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" data-testid="resa-search"
          className="ml-auto rounded-lg border border-neutral-200 px-3 py-1.5 text-sm outline-none focus:border-brand" />
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_380px]">
        <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
          {loading ? (
            <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
          ) : data.items.length === 0 ? (
            <p className="p-8 text-center text-sm text-neutral-500">Aucun dossier.</p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {data.items.map((r) => (
                <li key={r.id}>
                  <button onClick={() => openDetail(r.id)} data-testid={`resa-row-${r.id}`}
                    className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-neutral-50 ${selected?.id === r.id ? "bg-brand/5" : ""}`}>
                    <div className="min-w-0">
                      <p className="truncate font-bold text-neutral-900">{r.first_name} {r.last_name}</p>
                      <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-neutral-500">
                        <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {r.city}</span>
                        <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {r.month_label}</span>
                        <span>· {r.doc_count} doc(s)</span>
                      </p>
                    </div>
                    <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${STATUS_BADGE[r.status] || "bg-neutral-100"}`}>{r.status_label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm" data-testid="resa-detail">
          {!selected ? (
            <p className="py-10 text-center text-sm text-neutral-400">Sélectionne un dossier pour voir le détail.</p>
          ) : (
            <div>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-black text-neutral-900">{selected.first_name} {selected.last_name}</h2>
                <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${STATUS_BADGE[selected.status] || "bg-neutral-100"}`}>{selected.status_label}</span>
              </div>
              <p className="mt-2 text-sm text-neutral-600"><strong>{selected.offer_title}</strong> — {selected.city} · {selected.month_label}{selected.forfait_price != null ? ` · ${selected.forfait_price} €` : ""}</p>
              <dl className="mt-3 space-y-1 text-sm">
                <div className="flex items-center gap-2 text-neutral-600"><Mail className="h-4 w-4 text-brand" /> {selected.email}</div>
                <div className="flex items-center gap-2 text-neutral-600"><Phone className="h-4 w-4 text-brand" /> {selected.phone}</div>
                <div className="text-neutral-600">Auto-école : <strong>{selected.assigned_school_name || "—"}</strong></div>
                <div className="text-neutral-600">NEPH : <strong>{selected.has_neph ? (selected.neph_number || "oui") : selected.has_neph === false ? "non" : "—"}</strong> · Code : <strong>{selected.has_code === true ? "oui" : selected.has_code === false ? "non" : "—"}</strong></div>
                {selected.already_took_exam === true && <div className="mt-1"><span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800" data-testid="resa-retake-badge">🔁 Repassage : a déjà passé l'examen pratique</span></div>}
              </dl>

              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wide text-neutral-400">Documents ({(selected.documents || []).length})</p>
                <ul className="mt-2 space-y-2">
                  {(selected.documents || []).map((d) => (
                    <li key={d.id} className="flex items-center justify-between gap-2 rounded-lg bg-neutral-50 px-3 py-2 text-sm">
                      <span className="min-w-0 truncate text-neutral-700"><FileText className="mr-1 inline h-3.5 w-3.5 text-neutral-400" />{d.category === "code_result" ? "Relevé du code" : d.category} — {d.filename}</span>
                      <a href={`${BACKEND}/api/admin/reservations/${selected.id}/documents/${d.id}`} target="_blank" rel="noreferrer"
                        className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-brand/10 px-2.5 py-1 text-xs font-bold text-brand hover:bg-brand/20"><Download className="h-3.5 w-3.5" /> Ouvrir</a>
                    </li>
                  ))}
                  {(selected.documents || []).length === 0 && <li className="text-sm text-neutral-400">{selected.has_code ? "Relevé du code pas encore déposé." : "Aucun document requis (l'élève n'a pas encore le code)."}</li>}
                </ul>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {selected.status === "pending_docs" && (
                  <button disabled={acting || !selected.docs_complete} onClick={() => act(selected.id, "transfer")} data-testid="resa-transfer"
                    className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white hover:bg-brand-hover disabled:opacity-50">
                    <Send className="h-4 w-4" /> Transférer à l'auto-école
                  </button>
                )}
                {["pending_docs", "transmitted", "confirmed"].includes(selected.status) && (
                  <button disabled={acting} onClick={() => act(selected.id, "release")} data-testid="resa-release"
                    className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-bold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50">
                    <Unlock className="h-4 w-4" /> Libérer la place
                  </button>
                )}
                <button disabled={acting} onClick={() => act(selected.id, "delete")} data-testid="resa-delete"
                  className="inline-flex items-center gap-2 rounded-lg bg-red-50 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-100 disabled:opacity-50">
                  <Trash2 className="h-4 w-4" /> Supprimer
                </button>
              </div>

              <div className="mt-5" data-testid="resa-emails">
                <p className="text-xs font-bold uppercase tracking-wide text-neutral-400">Emails envoyés ({emails.length})</p>
                <ul className="mt-2 space-y-2">
                  {emails.map((m) => (
                    <li key={m.id} className="flex items-center justify-between gap-2 rounded-lg bg-neutral-50 px-3 py-2 text-sm" data-testid={`resa-email-${m.id}`}>
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-neutral-800">{m.subject}</p>
                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-neutral-500">
                          {m.status === "failed"
                            ? <AlertTriangle className="h-3 w-3 shrink-0 text-red-500" />
                            : <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-600" />}
                          {m.to} · {new Date(m.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                        </p>
                      </div>
                      <button onClick={() => setPreviewId(m.id)} data-testid={`resa-email-preview-${m.id}`}
                        className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-brand/10 px-2.5 py-1 text-xs font-bold text-brand hover:bg-brand/20"><Eye className="h-3.5 w-3.5" /> Voir</button>
                    </li>
                  ))}
                  {emails.length === 0 && <li className="text-sm text-neutral-400">Aucun email pour ce dossier.</li>}
                </ul>
              </div>

              {(selected.timeline || []).length > 0 && (
                <div className="mt-5">
                  <p className="text-xs font-bold uppercase tracking-wide text-neutral-400">Historique</p>
                  <ul className="mt-2 space-y-1 text-xs text-neutral-500">
                    {selected.timeline.slice().reverse().map((t, i) => <li key={i}>• {t.event}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <EmailPreview emailId={previewId} onClose={closePreview} />
    </Shell>
  );
}
